import React, { memo, useCallback, useMemo, useState } from 'react';
import TextFloatingFilter from 'ui/AgGridReact/TextFloatingFilter';
import AgGridReact from 'ui/AgGridReact/AgGridReact';
import { useGetHrPositionMappings } from 'routes/HumanResourcesModule/Settings/HrPositionMapping/helpers/hooks/hrPositionMapping';
import { useTranslation } from 'react-i18next';
import LoadingGrid from 'routes/HumanResourcesModule/components/LoadingGrid';
import {
    ColDef,
    GetRowIdParams,
    GridApi,
    GridReadyEvent,
} from 'ag-grid-community';
import PositionMappingHeader from 'routes/HumanResourcesModule/Settings/HrPositionMapping/components/PositionMappingHeader';
import { getColDefs } from 'routes/HumanResourcesModule/Settings/HrPositionMapping/helpers/colDefinitions';
import TooltipCellRenderer from 'routes/BudgetModule/components/BudgetOverview/BudgetSynthesisAndRatios/BudgetBalance/TooltipCellRenderer';
import HrMappingToggleCellRenderer from './components/cellRenderers/HrMappingToggleCellRenderer';
import HrMappingSelectorCellRenderer from './components/cellRenderers/HrMappingSelectorCellRenderer';
import { useHrIntegrationSoftwares } from 'helpers/integrations';
import HrCard from 'routes/HumanResourcesModule/components/HrCard';
import FullScreenProvider from 'contexts/FullScreenContextProvider';
import ConditionnalZoomIconButton from 'components/ConditionnalZoomIconButton';
import { isEqual } from 'lodash';
import { TRACKABLE_EVENTS_METADATA } from 'helpers/trackerHelpers';
import { HUMAN_RESOURCES } from 'business/constants';
import HelperAlert from 'ui/HelperAlert/HelperAlert';
import { CustomGridContainer } from 'routes/HumanResourcesModule/Settings/helpers/styles';

const DOC_LINK = 'https://docs.manty.eu/classification-des-agents';

function HrPositionMappingGrid(): React.ReactElement {
    const { t } = useTranslation();
    const [gridApi, setGridApi] = useState<GridApi | null>(null);
    const [selectedMappingsIds, setSelectedMappingsIds] = useState<number[]>(
        [],
    );
    const [isFullScreen, setIsFullScreen] = useState(false);

    const onGridReady = (event: GridReadyEvent) => {
        setGridApi(event.api);
    };
    const {
        data: positionMappingsData,
        isLoading: arePositionMappingsLoading,
    } = useGetHrPositionMappings();

    const positionMappings = useMemo(() => {
        if (positionMappingsData) {
            return [...positionMappingsData].sort((a, b) => a.id - b.id);
        }
        return [];
    }, [positionMappingsData]);

    const { data: integrations, isLoading: areIntegrationsLoading } =
        useHrIntegrationSoftwares();
    const firstIntegration = integrations?.values().next().value;

    const columnDefs: ColDef[] = useMemo(
        () => getColDefs(t, firstIntegration),
        [integrations],
    );

    const getRowId = useCallback(
        (params: GetRowIdParams) => String(params.data.id),
        [],
    );

    const onSelectionChanged = () => {
        if (gridApi) {
            const selectedRows = gridApi.getSelectedRows().map((row) => row.id);
            setSelectedMappingsIds(selectedRows);
        }
    };

    if (arePositionMappingsLoading || areIntegrationsLoading) {
        return <LoadingGrid />;
    }

    return (
        <FullScreenProvider
            module={HUMAN_RESOURCES}
            trackEventMetadata={{
                gridName:
                    TRACKABLE_EVENTS_METADATA.human_resources.fullScreen
                        .hrPositionMappingGrid,
            }}
        >
            <HrCard
                title={t('humanResources.settings.hrPositionMapping')}
                startAdornment={
                    <>
                        <ConditionnalZoomIconButton />{' '}
                    </>
                }
                setIsFullScreen={setIsFullScreen}
            >
                <HelperAlert>
                    <div>
                        <a href={DOC_LINK} target="_blank" rel="noreferrer">
                            {t('humanResources.settings.helpLink')}
                        </a>{' '}
                        <span>
                            {t('humanResources.settings.agentMappingHelpText')}
                        </span>
                    </div>
                </HelperAlert>
                {gridApi && (
                    <PositionMappingHeader
                        selectedMappingsIds={selectedMappingsIds}
                        gridApi={gridApi}
                    />
                )}
                <CustomGridContainer>
                    <AgGridReact
                        className="ag-theme-material"
                        domLayout={isFullScreen ? 'normal' : 'autoHeight'}
                        rowSelection="multiple"
                        suppressRowClickSelection
                        groupSelectsChildren
                        onGridReady={onGridReady}
                        rowData={positionMappings}
                        rowGroupPanelShow="always"
                        columnDefs={columnDefs}
                        rowHeight={32}
                        headerHeight={36}
                        getRowId={getRowId}
                        onSelectionChanged={onSelectionChanged}
                        pagination={!isFullScreen}
                        paginationPageSize={10}
                        components={{
                            textFloatingFilter: TextFloatingFilter,
                            tooltipCellRenderer: TooltipCellRenderer,
                            hrMappingToggleCellRenderer:
                                HrMappingToggleCellRenderer,
                            hrMappingSelectorCellRenderer:
                                HrMappingSelectorCellRenderer,
                        }}
                    />
                </CustomGridContainer>
            </HrCard>
        </FullScreenProvider>
    );
}
export default memo(HrPositionMappingGrid, isEqual);
